import React, { lazy, Suspense, memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider, Modal } from "antd";
import { lightTheme, darkTheme } from "configs/ThemeConfig";
import { resources } from "lang";
import useBodyClass from "utils/hooks/useBodyClass";
import Routes from "routes";
import { getSingleUser, getUserdata } from "store/slices/authSlice";
import { Encryption } from "store/slices/authSlice";
import { SUCCESS_CODE } from "constants/AppConstants";
import GlobalLoader from "components/shared-components/Loader/GlobalLoader";

const AppLayout = lazy(() => import("./AppLayout"));
const PhotographerLayout = lazy(() => import("./PhotographerLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));

const Layouts = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { singleUser, userData, loading, signinTriggered } = useSelector(
    (state) => state.auth
  );
  const blankLayout = useSelector((state) => state.theme.blankLayout);
  const locale = useSelector((state) => state.theme.locale);
  const direction = useSelector((state) => state.theme.direction);
  const currentTheme = useSelector((state) => state.theme.currentTheme);
  const { encryption } = useSelector((state) => state.auth);

  const currentAppLocale = resources[locale];
  useBodyClass(`dir-${direction}`);
  const themeConfig =
    currentTheme === "light" ? { ...lightTheme } : { ...darkTheme };

  useEffect(() => {
    const fetchEncryption = async () => {
      try {
        const result = await dispatch(Encryption({})).unwrap();
        console.log("Encryption result:", result);
        if (result.status === 503) return;
        if (result.status?.status_code != SUCCESS_CODE) {
          Modal.warn({
            title: "Please Try Again ",
            content: "Connection Error",
            okText: "Retry",
            onOk: () => {
              dispatch(Encryption({}));
            },
          });
        }
        console.log("Encryption success:", result);
      } catch (error) {
        console.error("Encryption failed:", error);
        Modal.warn({
          title: "Please Try Again ",
          content: "Connection Error",
          okText: "Retry",
          onOk: () => {
            dispatch(Encryption({}));
          },
        });
      }
    };

    fetchEncryption();
  }, [dispatch, encryption]);

  // 2️⃣ Fetch userData from token after encryption is ready
  useEffect(() => {
    const fetchUserData = async () => {
      if (token && !userData) {
        try {
          const result = await dispatch(getUserdata({})).unwrap();
          console.log("User data loaded:", result);
        } catch (error) {
          console.error("User data fetch failed:", error);
        }
      }
    };
    fetchUserData();
  }, [dispatch, token]);

  // 3️⃣ Fetch full user details after userData is available and encryption is still valid
  useEffect(() => {
    const fetchSingleUser = async () => {
      if (encryption != null && token && userData?.id) {
        try {
          await dispatch(getSingleUser({ user_id: userData.id }));
          console.log("Single user fetched.");
        } catch (error) {
          console.error("Fetching single user failed:", error);
        }
      }
    };
    fetchSingleUser();
  }, [dispatch, encryption, token, userData]);

  const getLayout = () => {
    if (!token || blankLayout) {
      return AuthLayout;
    }

    if (loading || !singleUser?.role) {
      return null;
    }

    switch (singleUser?.role?.position_id) {
      case 1:
        return AppLayout;
      case 3:
        return PhotographerLayout;
      default:
        return AppLayout;
    }
  };

  useEffect(() => {
    console.log("singleUser", singleUser);
  }, [singleUser]);

  const Layout = getLayout();
  const showLoading = token && (loading || !Layout);

  if (showLoading) {
    return (
      <ConfigProvider
        theme={themeConfig}
        direction={direction}
        locale={currentAppLocale.antd}
      >
        <GlobalLoader loading={true} cover="content" />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider
      theme={themeConfig}
      direction={direction}
      locale={currentAppLocale.antd}
    >
      <Suspense fallback={<GlobalLoader loading={true} cover="content" />}>
        {Layout && (
          <Layout>
            <Routes />
          </Layout>
        )}
      </Suspense>
    </ConfigProvider>
  );
};

export default memo(Layouts);
